<template>
  <v-container class="pa-4">
    <v-row align="center">
      <v-col>
        <month-paginator v-model="currentDate" :disabled="isLoading" />
      </v-col>
      <v-col cols="12" sm="auto" class="d-flex ga-2 flex-wrap">
        <v-btn
          variant="outlined"
          color="primary"
          :prepend-icon="mdiFilePdfBox"
          :disabled="isLoading"
          @click="openExportDialog('pdf')"
        >
          Exportar PDF
        </v-btn>
        <v-btn
          variant="outlined"
          color="primary"
          :prepend-icon="mdiFileTable"
          :disabled="isLoading"
          @click="openExportDialog('excel')"
        >
          Exportar Excel
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="exportDialog" max-width="480">
      <v-card>
        <v-card-title>Exportar Relatório Consolidado</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="exportTenantIds"
            :items="items"
            item-title="tenant_name"
            item-value="tenant_id"
            label="Clientes"
            hint="Deixe vazio para exportar todos"
            persistent-hint
            multiple
            chips
            closable-chips
            hide-details="auto"
            autocomplete="off"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="exportDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="exporting"
            @click="runConsolidatedExport"
          >
            Exportar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row v-if="isLoading">
      <v-col v-for="n in 4" :key="n" cols="6" sm="3">
        <v-skeleton-loader type="list-item-avatar-two-line" class="rounded-lg" />
      </v-col>
    </v-row>
    <v-row v-if="!isLoading && items.length">
        <v-col v-for="stat in summaryStats" :key="stat.label" cols="6" sm="3">
          <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg">
            <v-card-text class="d-flex align-center ga-3 py-3">
              <v-icon
                :icon="stat.icon"
                size="32"
                :color="stat.color"
                class="stat-icon"
              />
              <div>
                <div
                  class="text-h5 font-weight-bold"
                  :style="{ color: stat.color }"
                >
                  {{ stat.value }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ stat.label }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
    </v-row>

    <v-row v-if="isLoading">
      <v-col cols="12" md="7">
        <v-skeleton-loader type="image" height="348" class="rounded-lg" />
      </v-col>
      <v-col cols="12" md="5">
        <v-skeleton-loader type="image" height="348" class="rounded-lg" />
      </v-col>
    </v-row>
    <v-row v-else-if="items.length">
      <v-col cols="12" md="7">
        <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg h-100">
          <v-card-title class="text-subtitle-1">
            Documentos por Cliente
          </v-card-title>
          <v-card-text>
            <v-chart
              v-if="documentsChartOption.series"
              :option="documentsChartOption"
              :autoresize="true"
              style="height: 300px"
            />
            <div
              v-else
              class="d-flex align-center justify-center text-medium-emphasis"
              style="height: 300px"
            >
              Sem dados disponíveis
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg h-100">
          <v-card-title class="text-subtitle-1">
            Tipos de Dispositivo
          </v-card-title>
          <v-card-text>
            <v-chart
              v-if="deviceTypesChartOption.series"
              :option="deviceTypesChartOption"
              :autoresize="true"
              style="height: 300px"
            />
            <div
              v-else
              class="d-flex align-center justify-center text-medium-emphasis"
              style="height: 300px"
            >
              Sem dados disponíveis
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card :variant="isDark ? 'flat' : undefined" :color="isDark ? 'surface-bright' : undefined" class="rounded-lg">
          <v-card-title class="text-subtitle-1">Novos Clientes</v-card-title>
          <v-card-text>
            <v-skeleton-loader
              v-if="loadingTotalCompanies"
              type="image"
              height="300"
            />
            <v-chart
              v-else-if="newTenantsOption.series"
              :option="newTenantsOption"
              :autoresize="true"
              style="height: 300px"
            />
            <div
              v-else
              class="d-flex align-center justify-center text-medium-emphasis"
              style="height: 300px"
            >
              Sem dados disponíveis
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          class="billing-table rounded-lg"
          item-value="tenant_id"
          :headers="headers"
          :items="filteredTenants"
          :loading="isLoading"
          :items-per-page="50"
          :sort-by="[{ key: 'total_document_count', order: 'desc' }]"
          @click:row="onRowClick"
          hover
        >
          <template v-slot:top>
            <v-text-field
              v-model="search"
              :prepend-inner-icon="mdiMagnify"
              label="Buscar cliente"
              hide-details
              clearable
              class="pa-4"
            />
          </template>
          <template v-slot:item.tenant_cnpj="{ item }">
            {{ formatCnpj(item.tenant_cnpj) }}
          </template>
          <template v-slot:item.devices_by_type="{ item }">
            <v-chip
              v-for="device in item.devices_by_type"
              :key="device.device_type"
              size="small"
              class="mr-1"
            >
              {{ device.device_type_label }}: {{ device.count }}
            </v-chip>
          </template>
          <template v-slot:item.total_document_count="{ item }">
            {{ formatNumber(item.total_document_count) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex ga-1">
              <v-btn
                variant="text"
                icon
                size="small"
                @click.stop="exportMetrics(item.tenant_id, 'pdf')"
                :disabled="isLoading"
                title="Exportar PDF"
              >
                <v-icon :icon="mdiFilePdfBox" size="small" />
              </v-btn>
              <v-btn
                variant="text"
                icon
                size="small"
                @click.stop="exportMetrics(item.tenant_id, 'excel')"
                :disabled="isLoading"
                title="Exportar Excel"
              >
                <v-icon :icon="mdiFileTable" size="small" />
              </v-btn>
            </div>
          </template>
          <template v-slot:no-data>
            <div
              class="d-flex flex-column align-center pa-8 text-medium-emphasis"
            >
              <v-icon :icon="mdiFileSearchOutline" size="64" class="mb-4" />
              <div class="text-h6 mb-1">Nenhum cliente encontrado</div>
              <div class="text-body-2">
                Não há dados de billing para {{ monthLabel }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useTheme } from "vuetify";
import { useRoute, useRouter } from "vue-router";
import {
  mdiMagnify,
  mdiFilePdfBox,
  mdiFileTable,
  mdiFileSearchOutline,
  mdiAccountGroup,
  mdiStore,
  mdiAccessPoint,
  mdiFileDocumentMultiple,
} from "@mdi/js";
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  addMonths,
  parse,
  getYear,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import VChart from "vue-echarts";
import "@/plugins/echarts";
import config from "@/config";
import { formatNumberLocal } from "@/filters";
import MonthPaginator from "@/components/MonthPaginator.vue";
import { parseData } from "@/utils/dashboard";
import { useVendorTotalCompaniesQuery } from "@/queries/modules/vendor";

const SERIES_COLORS = [
  "#4f7cff",
  "#34d399",
  "#f472b6",
  "#fbbf24",
  "#818cf8",
  "#38bdf8",
  "#fb923c",
  "#a78bfa",
  "#2dd4bf",
  "#f87171",
  "#a3e635",
  "#e879f9",
];
import { useSnackbar } from "@/plugins/snackbar";
import {
  useBillingMetricsListQuery,
  exportBillingMetrics,
  exportConsolidatedBillingMetrics,
} from "@/queries/modules/billing";

const route = useRoute();
const router = useRouter();
const $snackbar = useSnackbar();
const theme = useTheme();
const isDark = computed(() => theme.global.current.value.dark);
const chartLabel = computed(() =>
  isDark.value ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.55)",
);
const chartGrid = computed(() =>
  isDark.value ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.08)",
);

const search = ref("");

const headers = [
  { title: "Cliente", key: "tenant_name" },
  { title: "Lojas", key: "company_count", align: "end" },
  { title: "Usuarios", key: "user_count", align: "end" },
  {
    title: "Dispositivos",
    key: "active_device_count",
    align: "end",
  },
  { title: "Tipos de dispositivo", key: "devices_by_type", sortable: false },
  {
    title: "Documentos",
    key: "total_document_count",
    align: "end",
  },
  { title: "", key: "actions", sortable: false, width: "96px" },
];

const initialDate = route.query.month
  ? parse(route.query.month, "yyyy-MM", new Date())
  : new Date();
const currentDate = ref(initialDate);

const filters = computed(() => {
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);
  return {
    date_start: format(monthStart, "yyyy-MM-dd"),
    date_end: format(monthEnd, "yyyy-MM-dd"),
    environment: "production",
  };
});

const { data: metricsData, isLoading } = useBillingMetricsListQuery(filters);

const items = ref([]);

watch(
  metricsData,
  (data) => {
    const tenants = data?.tenants || [];
    items.value = [...tenants]
      .sort((a, b) => a.tenant_name.localeCompare(b.tenant_name))
      .map((t) => ({ ...t }));
  },
  { immediate: true },
);

const filteredTenants = computed(() => {
  if (!search.value) return items.value;
  const term = search.value.toLowerCase();
  return items.value.filter((t) => t.tenant_name.toLowerCase().includes(term));
});

const monthLabel = computed(() => {
  const month = format(currentDate.value, "MMMM", { locale: ptBR });
  const year = getYear(currentDate.value);
  return `${month} de ${year}`;
});

watch(currentDate, () => {
  const month = format(currentDate.value, "yyyy-MM");
  router.replace({ query: { month } }).catch(() => {});
});

const onRowClick = (event, { item }) => {
  const month = format(currentDate.value, "yyyy-MM");
  router.push({
    name: "tenant.billing",
    params: { tenant_id: item.tenant_id },
    query: { month },
  });
};

// Summary stats
const summaryStats = computed(() => {
  const tenants = items.value;
  const totalCompanies = tenants.reduce((s, t) => s + t.company_count, 0);
  const totalDevices = tenants.reduce((s, t) => s + t.active_device_count, 0);
  const totalDocs = tenants.reduce((s, t) => s + t.total_document_count, 0);

  return [
    {
      label: "Clientes",
      value: formatNumberLocal(tenants.length),
      color: config.colors.primary,
      icon: mdiAccountGroup,
    },
    {
      label: "Lojas",
      value: formatNumberLocal(totalCompanies),
      color: "#818cf8",
      icon: mdiStore,
    },
    {
      label: "Dispositivos",
      value: formatNumberLocal(totalDevices),
      color: config.colors.teal,
      icon: mdiAccessPoint,
    },
    {
      label: "Documentos",
      value: formatNumberLocal(totalDocs),
      color: config.colors.danger,
      icon: mdiFileDocumentMultiple,
    },
  ];
});

// Documents per tenant chart (horizontal bar)
const documentsChartOption = computed(() => {
  const tenants = items.value;
  if (!tenants.length) return {};

  const sorted = [...tenants]
    .sort((a, b) => a.total_document_count - b.total_document_count)
    .slice(-10);

  return {
    color: SERIES_COLORS,
    tooltip: {
      trigger: "axis",
      appendToBody: true,
      axisPointer: { type: "shadow" },
      formatter(params) {
        const p = params[0];
        return `${p.name}<br/>${p.marker} Documentos: <b>${formatNumberLocal(p.value)}</b>`;
      },
    },
    grid: { left: 20, right: 20, top: 10, bottom: 10, containLabel: true },
    xAxis: {
      type: "value",
      splitLine: { lineStyle: { color: chartGrid.value } },
      axisLine: { show: false },
      axisLabel: {
        color: chartLabel.value,
        formatter(value) {
          if (value >= 1000) return (value / 1000).toFixed(0) + "k";
          return String(value);
        },
      },
    },
    yAxis: {
      type: "category",
      data: sorted.map((t) => t.tenant_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        width: 150,
        overflow: "truncate",
        color: chartLabel.value,
      },
    },
    series: [
      {
        type: "bar",
        colorBy: "data",
        data: sorted.map((t) => t.total_document_count),
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
});

// Device types pie chart (aggregated)
const deviceTypesChartOption = computed(() => {
  const tenants = items.value;
  if (!tenants.length) return {};

  const deviceMap = new Map();
  for (const tenant of tenants) {
    for (const device of tenant.devices_by_type || []) {
      const existing = deviceMap.get(device.device_type) || {
        label: device.device_type_label,
        count: 0,
      };
      existing.count += device.count;
      deviceMap.set(device.device_type, existing);
    }
  }

  const entries = Array.from(deviceMap.values());
  if (!entries.length) return {};

  return {
    color: SERIES_COLORS,
    tooltip: {
      trigger: "item",
      appendToBody: true,
      formatter: "{b}: {c} ({d}%)",
    },
    series: [
      {
        type: "pie",
        radius: ["40%", "70%"],
        center: ["50%", "50%"],
        label: {
          show: true,
          formatter: "{b}\n{d}%",
          color: chartLabel.value,
        },
        data: entries.map((d) => ({
          name: d.label,
          value: d.count,
        })),
      },
    ],
  };
});

const formatCnpj = (value) => {
  if (!value) return "";
  const cnpj = value.replace(/\D/g, "");
  if (cnpj.length !== 14) return value;
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

const formatNumber = (value) => {
  if (value == null) return "";
  return value.toLocaleString("pt-BR");
};

const getExportFilename = (response, tenantId, fmt) => {
  const disposition =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"];
  if (disposition) {
    const match = disposition.match(
      /filename\*=(?:UTF-8''|)([^;]+)|filename="?([^";]+)"?/i,
    );
    const filename = (match && (match[1] || match[2]))?.trim();
    if (filename) {
      try {
        return decodeURIComponent(filename);
      } catch {
        return filename;
      }
    }
  }
  const extension = fmt === "pdf" ? "pdf" : "xlsx";
  const month = format(currentDate.value, "yyyy-MM");
  return `faturamento-${tenantId}-${month}.${extension}`;
};

// New tenants chart
const vendorChartParams = ref({ date_start: null, date_end: null });

onMounted(() => {
  const now = new Date();
  vendorChartParams.value.date_start = format(
    startOfMonth(subMonths(now, 12)),
    "yyyy-MM-dd",
  );
  vendorChartParams.value.date_end = format(
    addMonths(startOfMonth(now), 1),
    "yyyy-MM-dd",
  );
});

const { data: totalCompaniesData, isLoading: loadingTotalCompanies } =
  useVendorTotalCompaniesQuery(vendorChartParams);

const parsedChartData = ref(null);

watch(totalCompaniesData, (data) => {
  if (data) {
    const res = parseData(data);
    parsedChartData.value = res.datasets.new_tenants || null;
  }
});

function formatTooltipDate(date, grouping) {
  if (grouping === "daily" || grouping === "weekly") {
    return format(date, "PP", { locale: ptBR });
  }
  return format(date, "MMMM 'de' yyyy", { locale: ptBR });
}

const newTenantsOption = computed(() => {
  const dataset = parsedChartData.value;
  if (!dataset?.datasets?.length) return {};

  const series = dataset.datasets[0];
  const xData = series.data.map((d) => d.x);
  const yData = series.data.map((d) => d.y);
  const grouping = series.grouping || "monthly";

  return {
    tooltip: {
      trigger: "axis",
      appendToBody: true,
      formatter(params) {
        const p = params[0];
        const date = new Date(p.axisValue);
        const title = formatTooltipDate(date, grouping);
        return `${title}<br/>${p.marker} ${p.seriesName}: <b>${p.value[1]}</b>`;
      },
    },
    grid: { left: 40, right: 20, top: 20, bottom: 30, containLabel: true },
    xAxis: {
      type: "time",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        color: chartLabel.value,
        formatter(value) {
          const date = new Date(value);
          if (grouping === "daily" || grouping === "weekly") {
            return format(date, "dd/MM/yyyy");
          }
          return format(date, "MMM yyyy", { locale: ptBR });
        },
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: chartGrid.value } },
      axisLabel: { color: "rgba(255,255,255,0.45)" },
    },
    series: [
      {
        name: series.label,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        itemStyle: { color: config.colors.primary },
        lineStyle: { width: 2, color: config.colors.primary },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: config.colors.primary + "40" },
              { offset: 1, color: config.colors.primary + "05" },
            ],
          },
        },
        data: xData.map((x, i) => [x, yData[i]]),
      },
    ],
  };
});

const exportDialog = ref(false);
const exportFormat = ref("pdf");
const exportTenantIds = ref([]);
const exporting = ref(false);

const openExportDialog = (fmt) => {
  exportFormat.value = fmt;
  exportTenantIds.value = [];
  exportDialog.value = true;
};

const getConsolidatedFilename = (response, fmt) => {
  const disposition =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"];
  if (disposition) {
    const match = disposition.match(
      /filename\*=(?:UTF-8''|)([^;]+)|filename="?([^";]+)"?/i,
    );
    const filename = (match && (match[1] || match[2]))?.trim();
    if (filename) {
      try {
        return decodeURIComponent(filename);
      } catch {
        return filename;
      }
    }
  }
  const extension = fmt === "pdf" ? "pdf" : "xlsx";
  const monthStart = format(startOfMonth(currentDate.value), "yyyyMMdd");
  const monthEnd = format(endOfMonth(currentDate.value), "yyyyMMdd");
  return `vendor-billing-consolidated-${monthStart}-${monthEnd}.${extension}`;
};

const runConsolidatedExport = async () => {
  exporting.value = true;
  const fmt = exportFormat.value;
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);

  try {
    const response = await exportConsolidatedBillingMetrics(fmt, {
      date_start: format(monthStart, "yyyy-MM-dd"),
      date_end: format(monthEnd, "yyyy-MM-dd"),
      environment: "production",
      "tenant_ids[]": exportTenantIds.value.length ? exportTenantIds.value : undefined,
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = getConsolidatedFilename(response, fmt);
    link.click();
    window.URL.revokeObjectURL(url);
    exportDialog.value = false;
  } catch (e) {
    $snackbar.error(`Erro ao exportar: ${e.message}`);
  } finally {
    exporting.value = false;
  }
};

const exportMetrics = async (tenantId, fmt) => {
  $snackbar.info("Baixando relatório...");
  const monthStart = startOfMonth(currentDate.value);
  const monthEnd = endOfMonth(currentDate.value);

  try {
    const response = await exportBillingMetrics(tenantId, fmt, {
      date_start: format(monthStart, "yyyy-MM-dd"),
      date_end: format(monthEnd, "yyyy-MM-dd"),
      environment: "production",
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = getExportFilename(response, tenantId, fmt);
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    $snackbar.error(`Erro ao exportar: ${e.message}`);
  }
};
</script>

<style scoped>
.billing-table :deep(tbody tr) {
  cursor: pointer;
}

.stat-icon {
  opacity: 0.18;
}
</style>
